import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../../services/dataService';
import { adminService } from '../../services/adminService';
import { MonthlyReport } from '../../types/domain';
import {
  buildProgressSummary,
  contextChartData,
  currentMonth,
  formatMonthLabel,
  formatNumber,
  formatSiteUse,
  glucoseChartData,
  hba1cChartData,
  inTargetPercent,
  insulinChartData,
  recentMonths
} from '../../lib/monthlyReport';
import {
  buildReportDocument,
  REPORT_CHART_IDS,
  reportFileName,
  ReportChartId
} from '../../lib/pdf/reportDocument';
import LazyTrendChart from '../components/charts/LazyTrendChart';
import LazyCategoryChart from '../components/charts/LazyCategoryChart';

/** Twelve months back is as far as a paediatric clinic ever looks. */
const MONTH_OPTIONS = 12;

export default function MonthlyReportPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const isClinician = profile?.role === 'doctor' || profile?.role === 'superadmin';
  // A clinician arrives from the dashboard with a patient in the URL; a patient
  // only ever sees their own, whatever the URL says.
  const patientId = isClinician ? params.get('patient') : user?.id ?? null;

  const [month, setMonth] = useState(() => {
    const requested = params.get('month');
    return requested ? `${requested.slice(0, 7)}-01` : currentMonth();
  });
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [patientName, setPatientName] = useState<string | null>(
    isClinician ? null : profile?.full_name ?? null
  );
  const [controlPlace, setControlPlace] = useState<string | null>(
    isClinician ? null : profile?.control_place ?? null
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chartRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const months = useMemo(() => recentMonths(MONTH_OPTIONS), []);

  useEffect(() => {
    if (!isClinician || !patientId) {
      return;
    }
    let cancelled = false;
    adminService
      .getPatientProfile(patientId)
      .then(found => {
        if (cancelled || !found) {
          return;
        }
        setPatientName(found.full_name ?? null);
        setControlPlace(found.control_place ?? null);
      })
      .catch(() => {
        // The report itself is what matters; an unnamed header is survivable.
      });
    return () => {
      cancelled = true;
    };
  }, [isClinician, patientId]);

  const load = useCallback(async () => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const found = isClinician
        ? await adminService.getPatientMonthlyReport(patientId, month)
        : await dataService.getMonthlyReport(patientId, month);
      setReport(found);
    } catch (err) {
      console.error(err);
      setError('Gagal memuat laporan bulanan.');
    } finally {
      setLoading(false);
    }
  }, [patientId, month, isClinician]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Builds the month on demand.
   *
   * Only ever needed for a month pg_cron has not reached — the month still
   * running, or one that predates the feature. The figures come from the same
   * SQL the cron job runs, so this never produces a second version of the
   * truth.
   */
  const handleGenerate = async () => {
    if (!patientId) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const built = await dataService.generateMonthlyReport(patientId, month);
      setReport(built);
      if (!built) {
        await load();
      }
    } catch (err: any) {
      setError(err?.message || 'Gagal menyusun laporan bulanan.');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!report) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Both the PDF engine and the capture helper are behind this import, so
      // a patient who never downloads never pays for jsPDF.
      const { captureChart, downloadReportPdf } = await import('../../lib/pdf/renderPdf');
      const captures: Record<string, Awaited<ReturnType<typeof captureChart>>> = {};
      for (const id of REPORT_CHART_IDS) {
        captures[id] = await captureChart(chartRefs.current[id]);
      }
      await downloadReportPdf(
        buildReportDocument(report, { fullName: patientName, controlPlace }),
        captures,
        reportFileName(patientName ?? 'Pasien', report.periodMonth)
      );
    } catch (err) {
      console.error(err);
      setError('Gagal membuat file PDF.');
    } finally {
      setBusy(false);
    }
  };

  const setChartRef = (id: ReportChartId) => (node: HTMLDivElement | null) => {
    chartRefs.current[id] = node;
  };

  if (isClinician && !patientId) {
    return (
      <div className="screen min-h-[100dvh] h-full">
        <div className="screen-card text-center">
          <div className="section-title">Laporan bulanan</div>
          <div className="subtitle">Pilih pasien dari dashboard terlebih dahulu.</div>
          <button type="button" className="btn-primary" onClick={() => navigate('/dashboard')}>
            Kembali ke dashboard
          </button>
        </div>
      </div>
    );
  }

  const metrics = report?.metrics ?? null;
  const glucoseSeries = metrics ? glucoseChartData(metrics) : [];
  const contextSeries = metrics ? contextChartData(metrics) : [];
  const insulinSeries = metrics ? insulinChartData(metrics) : [];
  const hba1cSeries = metrics ? hba1cChartData(metrics) : [];
  const inTarget = metrics ? inTargetPercent(metrics) : null;

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div className="screen-card" role="group" aria-labelledby="report-title">
        <div id="report-title" className="section-title">
          Laporan bulanan
        </div>
        <div className="subtitle">
          {patientName
            ? `${patientName} · ${formatMonthLabel(month)}`
            : formatMonthLabel(month)}
        </div>

        <div className="report-toolbar">
          <div className="field">
            <label htmlFor="report-month">Pilih bulan</label>
            <select
              id="report-month"
              value={month}
              onChange={event => setMonth(event.target.value)}
              disabled={busy}
            >
              {months.map(option => (
                <option key={option} value={option}>
                  {formatMonthLabel(option)}
                </option>
              ))}
            </select>
          </div>
          {report ? (
            <button
              type="button"
              className="btn-primary"
              onClick={handleDownload}
              disabled={busy}
            >
              {busy ? 'Menyiapkan...' : 'Unduh PDF'}
            </button>
          ) : null}
        </div>

        {error ? (
          <div className="target-band-error" role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="subtitle">Memuat laporan...</div>
        ) : !report ? (
          <div className="report-empty">
            <p>
              Laporan {formatMonthLabel(month)} belum tersedia. Laporan dibuat otomatis
              setiap awal bulan; untuk bulan yang sedang berjalan kamu bisa membuatnya
              sekarang.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={handleGenerate}
              disabled={busy}
            >
              {busy ? 'Menyusun...' : 'Buat laporan sekarang'}
            </button>
          </div>
        ) : (
          metrics && (
            <>
              <div className="report-meta">
                Dibuat {report.generatedAt.slice(0, 10)}
                {metrics.period.complete
                  ? ''
                  : ` · bulan berjalan, dihitung dari ${metrics.period.daysElapsed} hari pertama`}
                {' · '}
                <button
                  type="button"
                  className="report-refresh"
                  onClick={handleGenerate}
                  disabled={busy}
                >
                  perbarui
                </button>
              </div>

              <div className="report-stats">
                <ReportStat
                  label="Cek gula darah"
                  value={String(metrics.glucose.readings)}
                  note={`rata-rata ${formatNumber(metrics.glucose.average)} mg/dL`}
                />
                <ReportStat
                  label="Di rentang aman"
                  value={inTarget === null ? '–' : `${inTarget}%`}
                  note={`${metrics.glucose.hypoCount} hipo · ${metrics.glucose.hyperCount} hiper`}
                />
                <ReportStat
                  label="Total insulin"
                  value={`${formatNumber(metrics.insulin.totalUnits)} u`}
                  note={`${formatNumber(metrics.insulin.averageDailyUnits)} u/hari · ${metrics.insulin.injections} suntikan`}
                />
                <ReportStat
                  label="Kepatuhan catat"
                  value={`${metrics.compliance.percent}%`}
                  note={`${metrics.compliance.daysWithAnyEntry} dari ${metrics.compliance.daysElapsed} hari`}
                />
                <ReportStat
                  label="Titik suntik"
                  value={String(metrics.sites.distinctPoints)}
                  note={
                    metrics.sites.mostUsed.length
                      ? `terbanyak: ${formatSiteUse(metrics.sites.mostUsed[0])}`
                      : 'belum ada suntikan'
                  }
                />
                <ReportStat
                  label="Lipodistrofi"
                  value={String(metrics.sites.lipodystrophyCount)}
                  note="catatan benjolan / penebalan kulit"
                />
              </div>

              {glucoseSeries.length ? (
                <div className="report-chart" ref={setChartRef('glucose')}>
                  <LazyTrendChart
                    title="Gula darah harian"
                    subtitle="mg/dL"
                    data={glucoseSeries}
                    categoryKey="date"
                    categoryLabel="Tanggal"
                    series={[
                      { key: 'average', label: 'Rata-rata' },
                      { key: 'minimum', label: 'Terendah' },
                      { key: 'maximum', label: 'Tertinggi' }
                    ]}
                    unit=" mg/dL"
                    yDomain={[0, 'auto']}
                  />
                </div>
              ) : null}

              {contextSeries.length ? (
                <div className="report-chart" ref={setChartRef('context')}>
                  <LazyCategoryChart
                    title="Rata-rata per waktu pemeriksaan"
                    subtitle="mg/dL"
                    data={contextSeries}
                    categoryKey="context"
                    categoryLabel="Waktu pemeriksaan"
                    series={[{ key: 'average', label: 'Rata-rata' }]}
                    unit=" mg/dL"
                    layout="horizontal"
                    height={300}
                  />
                </div>
              ) : null}

              {insulinSeries.length ? (
                <div className="report-chart" ref={setChartRef('insulin')}>
                  <LazyTrendChart
                    title="Insulin harian"
                    subtitle="Unit"
                    data={insulinSeries}
                    categoryKey="date"
                    categoryLabel="Tanggal"
                    series={[{ key: 'units', label: 'Unit' }]}
                    unit=" u"
                    yDomain={[0, 'auto']}
                  />
                </div>
              ) : null}

              {hba1cSeries.length > 1 ? (
                <div className="report-chart" ref={setChartRef('hba1c')}>
                  <LazyTrendChart
                    title="Perubahan HbA1c"
                    subtitle="Persen (%)"
                    data={hba1cSeries}
                    categoryKey="date"
                    categoryLabel="Tanggal"
                    series={[{ key: 'hba1c', label: 'HbA1c' }]}
                    unit="%"
                    yDomain={[0, 'auto']}
                  />
                </div>
              ) : (
                <div className="subtitle report-note">
                  Grafik HbA1c muncul setelah ada minimal dua hasil pemeriksaan.
                </div>
              )}

              {metrics.insulin.byProduct.length ? (
                <div className="report-section">
                  <h3 className="report-section-title">Jenis insulin</h3>
                  <ul className="report-list">
                    {metrics.insulin.byProduct.map(row => (
                      <li key={row.label}>
                        <span>{row.label}</span>
                        <span>
                          {formatNumber(row.units)} u · {row.injections} suntikan
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {metrics.sites.mostUsed.length ? (
                <div className="report-section">
                  <h3 className="report-section-title">Lokasi paling sering dipakai</h3>
                  <ul className="report-list">
                    {metrics.sites.mostUsed.map(use => (
                      <li
                        key={`${use.regionId ?? 's'}-${use.pointRow ?? 0}-${use.pointCol ?? 0}-${use.siteId ?? 0}`}
                      >
                        <span>{formatSiteUse(use)}</span>
                        <span>{use.count}x</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="report-section">
                <h3 className="report-section-title">Ringkasan perkembangan</h3>
                <ul className="report-summary">
                  {buildProgressSummary(metrics).map(line => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

function ReportStat({
  label,
  value,
  note
}: {
  label: string;
  value: string;
  note: string;
}) {
  return (
    <div className="report-stat">
      <span className="report-stat-label">{label}</span>
      <span className="report-stat-value">{value}</span>
      <span className="report-stat-note">{note}</span>
    </div>
  );
}
